import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getHabboImageUrl } from '../HabboImage';
import { CURRENCY, SCRAP_UNIT, STATUS_META, relativeTime, formatDateTime } from '../../data/frankopop';

const PUB = process.env.PUBLIC_URL;
const PLACEHOLDER_FIGURE = `${PUB}/img/franklyshrug.png`;

// Default crate art shown in the shop for any set with no BoxArtURL of its own.
const CRATE_ART_URL = 'https://api.franklyorigins.net/uploads/1789459777160-franko_crate.png';
const API_URL = process.env.REACT_APP_API_URL;

// FrankoPop art is rendered by the imager, but the API fetches it server-side
// (GET frankopop/figures/:id/image) so the imager URL never reaches the browser.
// The response is shown as a blob: URL, cached per figure + packaging state for
// the life of the page. The key includes the figure's art so an admin edit
// shows the new render instead of the cached one.
const figureBlobCache = new Map();

function loadFigureBlobUrl(figure, sealed) {
    const key = `${figure.id}|${sealed ? 1 : 0}|${figure.figureString || ''}|${figure.image || ''}`;
    if (!figureBlobCache.has(key)) {
        const request = fetch(`${API_URL}frankopop/figures/${encodeURIComponent(figure.id)}/image${sealed ? '?boxed=1' : ''}`)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.blob();
            })
            .then((blob) => URL.createObjectURL(blob))
            .catch((err) => {
                figureBlobCache.delete(key);
                throw err;
            });
        figureBlobCache.set(key, request);
    }
    return figureBlobCache.get(key);
}

// Returns a blob: URL for the figure's art, null while it loads, or the
// placeholder when the figure has no art or the render fails.
export function useFigureImage(figure, sealed = false, enabled = true) {
    const hasArt = !!(figure && figure.id && (figure.figureString || figure.image));
    const [src, setSrc] = useState(null);

    useEffect(() => {
        if (!enabled) return undefined;
        if (!hasArt) { setSrc(PLACEHOLDER_FIGURE); return undefined; }
        let cancelled = false;
        setSrc(null);
        loadFigureBlobUrl(figure, sealed)
            .then((url) => { if (!cancelled) setSrc(url); })
            .catch(() => { if (!cancelled) setSrc(PLACEHOLDER_FIGURE); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, hasArt, figure?.id, figure?.figureString, figure?.image, sealed]);

    return src;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────────────
export const FRANKO_TABS = [
    { key: 'shop', label: 'FrankoPop Shop', to: '/frankopop/shop' },
    { key: 'mine', label: 'My Collection', to: '/frankopop/my-collection' },
    { key: 'activity', label: 'Activity', to: '/frankopop/activity' },
    { key: 'marketplace', label: 'Marketplace', to: '/frankopop/marketplace' },
    { key: 'trades', label: 'My Trades', to: '/frankopop/trades' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Buttons
// ─────────────────────────────────────────────────────────────────────────────
const BTN_BASE = "font-['Jersey_10'] uppercase tracking-[1px] transition-all disabled:opacity-40 disabled:cursor-not-allowed";
const BTN_VARIANTS = {
    primary: 'text-white hover:opacity-90',
    secondary: 'bg-gray-300 hover:bg-gray-400 text-gray-800',
    ghost: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100',
    gold: 'text-gray-900 hover:opacity-90',
    danger: 'text-white hover:opacity-90',
};

export function FpButton({ variant = 'primary', size = 'md', className = '', style, children, ...rest }) {
    const pad = size === 'sm' ? 'px-3 py-1 text-[14px]' : size === 'lg' ? 'px-7 py-2.5 text-[19px]' : 'px-5 py-2 text-[16px]';
    const vStyle =
        variant === 'primary' ? { backgroundColor: '#5A2929', boxShadow: '0 3px 0 0 rgba(0,0,0,0.3)' }
        : variant === 'gold' ? { backgroundColor: '#ffcc66', boxShadow: '0 3px 0 0 rgba(0,0,0,0.3)' }
        : variant === 'danger' ? { backgroundColor: '#b91c1c', boxShadow: '0 3px 0 0 rgba(0,0,0,0.3)' }
        : { boxShadow: '0 3px 0 0 rgba(0,0,0,0.25)' };
    return (
        <button className={`${BTN_BASE} ${BTN_VARIANTS[variant]} ${pad} ${className}`} style={{ ...vStyle, ...style }} {...rest}>
            {children}
        </button>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────────────────────
export function FpModal({ title, onClose, children, maxWidth = 'max-w-md', zIndex = 200 }) {
    return (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex }}>
            <div className="absolute inset-0 bg-black bg-opacity-60" onClick={onClose} />
            <div
                className={`relative bg-offwhite border-2 border-gray-800 p-6 w-full mx-4 overflow-y-auto max-h-[90vh] ${maxWidth}`}
                style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.5)', zIndex: zIndex + 1 }}
            >
                {title && (
                    <>
                        <h2 className="text-maroon uppercase font-['Jersey_10'] tracking-[1px] text-[20px] mb-3">{title}</h2>
                        <hr className="border-gold mb-4" />
                    </>
                )}
                {children}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wallet chip + page layout (hero + tab strip)
// ─────────────────────────────────────────────────────────────────────────────
export function WalletChip({ balance }) {
    return (
        <div className="flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-black/25" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)' }}>
            <img src={`${PUB}/img/frampcard_blank.png`} alt="" className="w-5 h-5" style={{ imageRendering: 'pixelated' }} />
            <span className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gold">
                {balance == null ? '-' : balance} {CURRENCY}
            </span>
        </div>
    );
}

export function ScrapsChip({ scraps }) {
    return (
        <div className="flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-black/25" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)' }}>
            <span className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gold">
                {scraps == null ? '-' : scraps} {SCRAP_UNIT}
            </span>
        </div>
    );
}

// `scraps` is optional - pages where scraps matter (trading) pass it to show a
// second chip next to the framp card balance.
export function FrankoPopLayout({ active, wallet, scraps, children }) {
    return (
        <div className="w-full min-h-screen p-4 lg2:p-6">
            <div className="max-w-[1200px] mx-auto">
                <div className="border border-black mb-4 overflow-hidden" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.4)' }}>
                    <div
                        className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                        style={{
                            backgroundImage: `linear-gradient(135deg, rgba(90,41,41,0.96) 0%, rgba(61,26,26,0.92) 45%, rgba(138,96,16,0.85) 78%, rgba(255,204,102,0.7) 100%), url(${PUB}/img/console-bg-darker.png)`,
                            backgroundSize: 'cover, auto',
                            backgroundRepeat: 'no-repeat, repeat',
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <img src={`${PUB}/img/box.png`} alt="" className="w-9 h-9" style={{ imageRendering: 'pixelated', filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.4))' }} />
                            <div>
                                <h1
                                    className="font-['Jersey_10'] uppercase text-[30px] leading-none tracking-[2px] text-white"
                                    style={{ textShadow: '-1px 0 0 #000, 1px 0 0 #000, 0 -1px 0 #000, 0 2px 0 rgba(0,0,0,0.5)' }}
                                >
                                    Franko<span className="text-gold">Pop</span>
                                </h1>
                                <p className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-white/70 mt-1">
                                    Blind boxes · vinyl figures · full-set glory
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <WalletChip balance={wallet} />
                            {scraps !== undefined && <ScrapsChip scraps={scraps} />}
                        </div>
                    </div>

                    <nav className="flex flex-wrap gap-1.5 px-3 py-2 bg-coal" style={{ backgroundImage: `url(${PUB}/img/console-bg-darker.png)` }}>
                        {FRANKO_TABS.map((tab) => {
                            const isActive = tab.key === active;
                            return (
                                <Link
                                    key={tab.key}
                                    to={tab.to}
                                    className={`font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-3 py-1.5 rounded transition-all ${
                                        isActive ? 'bg-gold text-gray-900' : 'text-white/80 hover:bg-black/30 hover:text-white'
                                    }`}
                                    style={isActive ? { boxShadow: '0 2px 0 0 rgba(0,0,0,0.35)' } : { textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                                >
                                    {tab.label}
                                </Link>
                            );
                        })}
                    </nav>
                </div>

                {children}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Rarity pill / status pill
// ─────────────────────────────────────────────────────────────────────────────
export function RarityPill({ name, colour, className = '' }) {
    return (
        <span
            className={`inline-block font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-white px-2 py-0.5 ${className}`}
            style={{ backgroundColor: colour || '#6b7280', boxShadow: '0 2px 0 0 rgba(0,0,0,0.25)' }}
        >
            {name || 'Unknown'}
        </span>
    );
}

export function StatusPill({ status, className = '' }) {
    const meta = STATUS_META[status] || { label: status, colour: '#6b7280' };
    return (
        <span
            className={`inline-block font-['Jersey_10'] uppercase text-[10px] tracking-[1px] text-white px-1.5 py-0.5 ${className}`}
            style={{ backgroundColor: meta.colour }}
        >
            {meta.label}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Artwork - real image when set, CSS placeholder otherwise
// ─────────────────────────────────────────────────────────────────────────────
export function BoxArt({ set, size = 150 }) {
    const src = set?.boxArt || CRATE_ART_URL;
    return (
        <div className="border-2 border-black bg-gray-100 flex items-center justify-center shrink-0" style={{ width: size, height: size, boxShadow: '0 4px 0 rgba(0,0,0,0.35)' }}>
            <img src={src} alt="" className="max-w-full max-h-full object-contain" style={{ imageRendering: 'pixelated' }} />
        </div>
    );
}

export function SetArt({ set, size = 120, wide = false }) {
    const style = wide ? { width: '100%', height: size } : { width: size, height: size };
    if (set?.setArt) {
        return (
            <div className="border-2 border-black bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden" style={{ ...style }}>
                <img src={set.setArt} alt="" className="w-full h-full object-cover" style={{ imageRendering: 'pixelated' }} />
            </div>
        );
    }
    return (
        <div
            className="relative flex items-center justify-center border-2 border-black shrink-0 overflow-hidden"
            style={{ ...style, background: `linear-gradient(120deg, ${set?.tint || '#5A2929'}, ${set?.accent || '#ffcc66'})` }}
        >
            <div className="absolute inset-0 opacity-25" style={{ background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.2) 0 10px, transparent 10px 20px)' }} />
            <span className="relative font-['Jersey_10'] uppercase text-white tracking-[2px] text-center px-3 leading-none" style={{ fontSize: 20, textShadow: '0 2px 0 rgba(0,0,0,0.4)' }}>
                {set?.name || 'Set art'}
            </span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Figure display frame - the "boxed vinyl" look
// ─────────────────────────────────────────────────────────────────────────────
export function FigureFrame({ figure, sealed = false, owned = true, size = 150 }) {
    const artSrc = useFigureImage(figure, sealed, !!figure && owned);
    if (!figure) return null;

    if (!owned) {
        return (
            <div className="flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
                <span className="font-['Jersey_10'] text-gray-300 leading-none text-4xl">?</span>
            </div>
        );
    }

    return (
        <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
            {artSrc && <img src={artSrc} alt={figure.name} />}
            {figure.ltd && (
                <>
                    <span className="fp-sparkle" style={{ top: '10%', left: '14%', animationDelay: '0s' }}>✦</span>
                    <span className="fp-sparkle" style={{ top: '20%', right: '12%', animationDelay: '0.6s' }}>✦</span>
                    <span className="fp-sparkle" style={{ bottom: '16%', left: '22%', animationDelay: '1.2s' }}>✦</span>
                    <style>{`
                        .fp-sparkle {
                            position: absolute;
                            color: #ffd966;
                            text-shadow: 0 0 4px rgba(255,217,102,0.9), 0 0 8px rgba(255,217,102,0.5);
                            font-size: 14px;
                            line-height: 1;
                            pointer-events: none;
                            animation: fp-sparkle-twinkle 1.8s ease-in-out infinite;
                        }
                        @keyframes fp-sparkle-twinkle {
                            0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
                            50% { opacity: 1; transform: scale(1) rotate(20deg); }
                        }
                    `}</style>
                </>
            )}
        </div>
    );
}

export function FigureCard({ figure, sealed = false, owned = true, corner, footer, onClick, frameSize = 160 }) {
    const colour = figure?.rarityColour || '#6b7280';
    return (
        <div
            className={`bg-white border border-gray-300 flex flex-col overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-[0_3px_0_0_rgba(0,0,0,0.5)]' : ''}`}
            style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.15)', transition: 'box-shadow 0.15s' }}
            onClick={onClick}
        >
            <div className="relative bg-gray-100 border-b border-gray-200 flex items-center justify-center p-2">
                <FigureFrame figure={figure} sealed={sealed} owned={owned} size={frameSize} />
                {corner && (
                    <span className="absolute top-2 right-2 font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-white px-2 py-0.5" style={{ backgroundColor: '#5A2929', boxShadow: '0 2px 0 0 rgba(0,0,0,0.25)' }}>
                        {corner}
                    </span>
                )}
            </div>
            <div className="flex flex-col flex-1 p-3 gap-2">
                <div>
                    <h3 className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-900 leading-tight">
                        {owned ? figure.name : '???'}
                    </h3>
                    {figure.ltd && (
                        <p className="text-[11px] text-maroon font-['Jersey_10'] uppercase tracking-[1px]">LTD {figure.ltdTotal}</p>
                    )}
                </div>
                {owned
                    ? <RarityPill name={figure.rarityName} colour={colour} className="self-start" />
                    : <span className="inline-block self-start font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-white px-2 py-0.5" style={{ backgroundColor: '#9ca3af', boxShadow: '0 2px 0 0 rgba(0,0,0,0.25)' }}>Not collected</span>}
                {footer && <div className="mt-auto pt-1">{footer}</div>}
            </div>
            <div className="h-1" style={{ backgroundColor: owned ? colour : '#d1d5db' }} />
        </div>
    );
}

export const habboHeadUrl = (habboName, hotel = 'COM') => (habboName
    ? `${getHabboImageUrl({ habbo: habboName, hotel: hotel || 'COM', direction: 2, head_direction: 2 })}&head_only=true`
    : null);

// Habbo head + linked name, used on trade cards.
export function HabboTag({ user, sub }) {
    const name = user?.habboName || 'Someone';
    const headUrl = habboHeadUrl(user?.habboName, user?.hotel);
    return (
        <div className="flex items-center gap-2 min-w-0">
            {headUrl && <img src={headUrl} alt={name} className="shrink-0" />}
            <div className="min-w-0">
                {user?.habboName ? (
                    <Link to={`/members/profile/${encodeURIComponent(user.habboName)}`} className="font-bold text-sm text-gray-900 hover:underline">{name}</Link>
                ) : (
                    <span className="font-bold text-sm text-gray-900">{name}</span>
                )}
                {sub && <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Activity feed row - habbo on the left, what happened, the FrankoPop on the right
// (still in its packaging for a pull, out of it for an unpackage).
// ─────────────────────────────────────────────────────────────────────────────
export function ActivityRow({ event }) {
    const pulled = event.type === 'minted';
    const name = event.habboName || 'Someone';
    const figureName = event.figure?.name || 'a FrankoPop';
    const headUrl = habboHeadUrl(event.habboName, event.hotel);
    const figureUrl = useFigureImage(event.figure, pulled);

    return (
        <div className="bg-white border border-gray-300 flex items-center gap-3 px-3 py-2" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.12)' }}>
            <div className="shrink-0">
                {headUrl && <img src={headUrl} alt={name} />}
            </div>

            <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 leading-snug">
                    {event.habboName ? (
                        <Link to={`/members/profile/${encodeURIComponent(event.habboName)}`} className="font-bold text-gray-900 hover:underline">{name}</Link>
                    ) : (
                        <span className="font-bold text-gray-900">{name}</span>
                    )}
                    {pulled ? ' pulled ' : ' took '}
                    <span className="font-bold text-maroon">{figureName}</span>
                    {pulled
                        ? <>{' from '}<span className="font-bold text-gray-900">{event.setName || 'a set'}</span></>
                        : ' out of its packaging'}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                    <RarityPill name={event.figure?.rarityName} colour={event.figure?.rarityColour} />
                    {event.figure?.ltd && (
                        <span className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-maroon">
                            LTD #{event.serialNumber} of {event.figure.ltdTotal}
                        </span>
                    )}
                    <span className="text-[11px] text-gray-400" title={formatDateTime(event.at)}>{relativeTime(event.at)}</span>
                </div>
            </div>

            <div className="shrink-0">
                {figureUrl && <img src={figureUrl} alt={figureName} />}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pagination - numbered pages with Prev/Next
// ─────────────────────────────────────────────────────────────────────────────
// Page numbers around the current page, plus first/last, with '...' for gaps.
function pageList(page, totalPages, delta = 2) {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) pages.push(i);
    }
    const withEllipsis = [];
    let prev = null;
    for (const p of pages) {
        if (prev && p - prev > 1) withEllipsis.push('...');
        withEllipsis.push(p);
        prev = p;
    }
    return withEllipsis;
}

const PAGE_BTN = "px-3 py-1 font-['Jersey_10'] uppercase text-[14px] tracking-[1px] rounded transition-colors disabled:opacity-40";
const PAGE_BTN_SHADOW = { boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' };

export function Pagination({ page, totalPages, total, onChange, noun = 'event' }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4">
            <p className="text-xs text-gray-400">
                Page {page} of {totalPages} - {total} {noun}{total !== 1 ? 's' : ''}
            </p>
            <div className="flex flex-wrap items-center gap-1">
                <button
                    onClick={() => onChange(page - 1)}
                    disabled={page === 1}
                    className={`${PAGE_BTN} bg-gray-200 text-gray-700 hover:bg-gray-300`}
                    style={PAGE_BTN_SHADOW}
                >
                    Prev
                </button>
                {pageList(page, totalPages).map((p, i) =>
                    p === '...' ? (
                        <span key={`ellipsis-${i}`} className="px-2 text-gray-500 font-['Jersey_10'] text-[14px]">...</span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => onChange(p)}
                            className={`${PAGE_BTN} ${p === page ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            style={PAGE_BTN_SHADOW}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    onClick={() => onChange(page + 1)}
                    disabled={page === totalPages}
                    className={`${PAGE_BTN} bg-gray-200 text-gray-700 hover:bg-gray-300`}
                    style={PAGE_BTN_SHADOW}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress bar / stat tile / empty state / lock banner
// ─────────────────────────────────────────────────────────────────────────────
export function ProgressBar({ value, max, color = '#5A2929', height = 10 }) {
    const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
        <div className="w-full bg-gray-200 border border-gray-300 overflow-hidden" style={{ height }}>
            <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color, boxShadow: 'inset 0 -3px 0 rgba(0,0,0,0.15)' }} />
        </div>
    );
}

export function StatTile({ label, value, sub, accent = '#5A2929' }) {
    return (
        <div className="bg-white border border-gray-300 px-3 py-2" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.12)' }}>
            <p className="font-['Jersey_10'] uppercase text-[12px] tracking-[1px] text-gray-500 leading-none">{label}</p>
            <p className="font-['Jersey_10'] text-[24px] leading-tight" style={{ color: accent }}>{value}</p>
            {sub && <p className="text-[11px] text-gray-400 leading-none">{sub}</p>}
        </div>
    );
}

export function EmptyState({ title, sub, action }) {
    return (
        <div className="text-center py-12 px-4">
            <img src={`${PUB}/img/franklyshrug.png`} alt="" className="mx-auto mb-3 opacity-70" style={{ imageRendering: 'pixelated', maxWidth: 120 }} />
            <p className="font-['Jersey_10'] uppercase text-[18px] tracking-[1px] text-gray-700">{title}</p>
            {sub && <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">{sub}</p>}
            {action && <div className="mt-4">{action}</div>}
        </div>
    );
}

export function LockBanner({ children }) {
    return (
        <div className="flex items-start gap-3 border-2 border-amber-500 bg-amber-50 px-4 py-3 mb-4">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" className="shrink-0 mt-0.5">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <p className="text-sm text-amber-800 leading-snug">{children}</p>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Form field primitives (admin/staff pages)
// ─────────────────────────────────────────────────────────────────────────────
const LABEL = "block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-1";
const INPUT = "w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm disabled:bg-gray-100 disabled:text-gray-400";

export function Field({ label, hint, disabled, required, children }) {
    return (
        <div className={disabled ? 'opacity-60' : ''}>
            <label className={LABEL}>{label}{required && <span className="text-red-500"> *</span>}</label>
            {children}
            {hint && <p className="mt-1 text-[11px] text-gray-400">{hint}</p>}
        </div>
    );
}

export function TextInput(props) {
    return <input type="text" {...props} className={`${INPUT} ${props.className || ''}`} />;
}
export function NumberInput(props) {
    return <input type="number" {...props} className={`${INPUT} ${props.className || ''}`} />;
}
export function DateTimeInput(props) {
    return <input type="datetime-local" {...props} className={`${INPUT} ${props.className || ''}`} />;
}
export function Select({ children, ...props }) {
    return <select {...props} className={`${INPUT} ${props.className || ''}`}>{children}</select>;
}

export function Checkbox({ label, checked, onChange, disabled }) {
    return (
        <label className={`flex items-center gap-2 cursor-pointer select-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-maroon" />
            <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700">{label}</span>
        </label>
    );
}

export function RadioRow({ options, value, onChange, disabled }) {
    return (
        <div className={`flex flex-wrap gap-2 ${disabled ? 'opacity-50' : ''}`}>
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(o.value)}
                    className={`font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1.5 rounded transition-all ${
                        value === o.value ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}
